import { useCallback, useEffect, useMemo, useRef, useState } from "react"; import type { ChangeEvent, FormEvent, ReactElement } from "react";
import { API_BASE_URL, FALLBACK_LANGUAGE } from "../../../../config";
import { getAuthHeaders } from "../../../../api/auth";
import {
  formatResultValue,
  type FieldFormatDescriptor
} from "../../../../standalone/formatting";
import { formatTimingStatus, isTimingStatus } from "../../../../standalone/timingStatus";
import type { TimingStatus } from "../../../../standalone/types";
import {
  listTableVariants,
  getDefaultTableVariant,
  createTableVariant,
  updateTableVariant,
  deleteTableVariant,
  type TableVariant,
} from "../../../../api/tableVariants";
import { YardCalendarView } from "../../../../components/YardCalendarView";
import "./YardListPage.css";

// TenantId wird jetzt aus dem JWT Token extrahiert (Backend holt es automatisch)
const APPLICATION_ID = "yardDocumentList";

// Lokale Typen für die Yard-spezifische Varianten-Konfiguration
interface YardVariantFilters {
  transportNumber?: string;
  fractions?: string[];
  carrierName?: string;
  supplierName?: string;
  statuses?: string[]; // SAP LKW_LOAD_STATUS: string values "1"-"15"
  [key: string]: unknown; // Index signature for Record compatibility
}

interface YardVariantSort {
  key: string;
  direction: "asc" | "desc";
}

interface YardDocument extends Record<string, unknown> {
  _id: string;
  TRANSPORTCHARGE?: string | null;
  LKW_LOAD_ID?: string | null;
  driverLanguage?: string | null;
  WERK2?: string | null;
  KENNZEICHEN?: string | null;
  KENNZEICHEN_ANHAENGER?: string | null;
  LKW_LOAD_STATUS?: string | null; // SAP status: "1"-"15"
  timingStatus?: TimingStatus | null;
  FRAKTION?: string | null;
  HOLZGRUPPE?: string | null;
  SORTIMENT?: string | null;
  SPEDITION?: string | null;
  LIEFERANT?: string | null;
  DATUM_CHECKIN?: string | null;
  DATUM_ANNAHME?: string | null;
  DATUM_UEBERGABE?: string | null;
  DATUM_UEBERNAHME?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  calledAt?: string | null; // Timestamp when status changed to "2" (Abruf erfolgt)
  fraction?: string | null; // Material/fraction from pre-checkin (lowercase)
  // Legacy for backward compatibility (might still be in some responses)
  transportNumber?: string | null;
}

interface YardDocumentListResponse {
  total: number;
  items: YardDocument[];
}

interface ColumnDefinition {
  key: keyof YardDocument | "timeWindowFrom" | "timeWindowTo" | "createdAt";
  label: string;
  width?: number;
  format?: FieldFormatDescriptor | null;
  type?: "status";
  sortable?: boolean;
}

type SortDirection = "asc" | "desc";

interface SortConfig {
  key: ColumnDefinition["key"];
  direction: SortDirection;
}

const DATETIME_FORMAT: FieldFormatDescriptor = { kind: "datetime" };
const DATA_SOURCE_KEY = "yardDocuments";
const COMPOSITION_IDENTIFIER_CANDIDATES = ["yardDocumentList", "yard-document-list"] as const;
const DEFAULT_SORT_PARAM = "createdAt:desc";
const ACTION_FEEDBACK_DURATION_MS = 5000;
const YARD_STATUS_LABELS: Record<string, string> = {
  "1": "Pre-Check-In",
  "2": "Abruf erfolgt",
  "3": "Check-In Start",
  "6": "Check-In Ende",
  "9": "Check-Out Start",
  "10": "Check-Out QS Start",
  "11": "Check-Out QS Ende",
  "12": "Check-Out Ende",
  "13": "Warehouse Transports Check-In",
  "14": "Warehouse Transports Check-Out",
  "15": "Warehouse Transports"
};

interface WeightConfirmationItem {
  id: string;
  transportBatchNumber: string;
  direction: "checkin" | "checkout";
  weight: number;
  licensePlate: string | null;
  status: "pending" | "confirmed" | "superseded";
  createdAt: string;
  confirmedAt: string | null;
  confirmedBy: string | null;
  batchTransportId: string | null;
  calibrationNumber: string | null;
  manuallyAdjusted: boolean;
}

const WEIGHT_CONFIRMATION_POLL_INTERVAL_MS = 4000;
const WEIGHT_CONFIRMATION_FETCH_LIMIT = 10;

const WEIGHT_CONFIRMATION_TEXT = {
  title: "Gewicht bestätigen",
  description: "Bitte bestätige das verwogene Gewicht, bevor der Fahrer fortfahren kann.",
  transportLabel: "Chargennummer",
  licensePlateLabel: "Kennzeichen",
  processLabel: "Prozess",
  weightLabel: "Gewicht",
  createdAtLabel: "Anfragezeit",
  queueHintSingle: "Eine weitere Anfrage wartet.",
  queueHintPlural: (count: number) => `${count} weitere Anfragen warten.`,
  confirmButton: "Gewicht bestätigen",
  confirmingButton: "Bestätigung läuft …",
  editButton: "Bearbeiten",
  saveButton: "Speichern",
  cancelButton: "Abbrechen",
  direction: {
    checkin: "Check-In",
    checkout: "Check-Out"
  } as const,
  defaultError: "Bestätigung fehlgeschlagen. Bitte erneut versuchen.",
  missingError: "Anfrage existiert nicht mehr. Sie wurde möglicherweise bereits bestätigt.",
  fetchError: "Ausstehende Gewichtsbestätigungen konnten nicht geladen werden."
} as const;

const COLUMNS: ColumnDefinition[] = [
  { key: "LKW_LOAD_STATUS", label: "Status", width: 80 },
  { key: "LKW_LOAD_ID", label: "Transportnummer", width: 130 },
  { key: "KENNZEICHEN", label: "LKW-Kennzeichen", width: 100 },
  { key: "FRAKTION", label: "Fraktion", width: 80 },
  { key: "HOLZGRUPPE", label: "Holzgruppe", width: 90 },
  { key: "SORTIMENT", label: "Sortiment", width: 110 },
  { key: "SPEDITION", label: "Spediteur", width: 130 },
  { key: "LIEFERANT", label: "Lieferant", width: 150 },
  { key: "timingStatus", label: "Pünktlichkeit", width: 100, type: "status", sortable: false },
  { key: "timeWindow", label: "Zeitfenster", width: 150, sortable: false },
  { key: "createdAt", label: "Ankunft", width: 80 },
  { key: "waitTime", label: "Wartezeit (Min.)", width: 80, sortable: false }
];

// Material Overview Summary Configuration
interface MaterialSummaryRow {
  label: string;
  fractionMatches: string[]; // Case-insensitive matching
  maxOnYard: number;
  countParkplatz: number; // Status "1"
  countWerk: number; // Status "2"-"7"
}

const MATERIAL_CONFIG: Omit<MaterialSummaryRow, 'countParkplatz' | 'countWerk'>[] = [
  { label: "Waldholz", fractionMatches: ["waldholz"], maxOnYard: 5 },
  { label: "Brennmaterial", fractionMatches: ["brennmaterial"], maxOnYard: 4 },
  { label: "Recycl.HKS & Hackschnitzel", fractionMatches: ["hackschnitzel", "recycl. hks"], maxOnYard: 3 },
  { label: "Staub", fractionMatches: ["staub"], maxOnYard: 2 },
  { label: "Vorbruch & Sägerestholz", fractionMatches: ["vorbruch", "sägerestholz"], maxOnYard: 1 },
];

function calculateMaterialSummary(documents: YardDocument[]): MaterialSummaryRow[] {
  return MATERIAL_CONFIG.map((config) => {
    let countParkplatz = 0;
    let countWerk = 0;

    for (const doc of documents) {
      // Check both SAP FRAKTION field and legacy fraction field
      const fractionSap = (doc.FRAKTION || "").toLowerCase().trim();
      const fractionLegacy = (doc.fraction || "").toLowerCase().trim();
      const fraction = fractionSap || fractionLegacy; // Prefer SAP field
      const status = doc.LKW_LOAD_STATUS;

      // Check if fraction matches any of the configured values (case-insensitive)
      const matches = config.fractionMatches.some((match) =>
        fraction.includes(match.toLowerCase())
      );

      if (matches && status) {
        if (status === "1") {
          countParkplatz++;
        } else if (["2", "3", "4", "5", "6", "7"].includes(status)) {
          countWerk++;
        }
      }
    }

    return {
      ...config,
      countParkplatz,
      countWerk,
    };
  });
}

function parseSortParam(param: string | null): SortConfig | null {
  if (!param) {
    return null;
  }

  const [rawKey, rawDirection] = param.split(":", 2);
  if (!rawKey || !rawDirection) {
    return null;
  }

  const column = COLUMNS.find((candidate) => String(candidate.key) === rawKey);
  if (!column) {
    return null;
  }

  if (rawDirection === "asc" || rawDirection === "desc") {
    return { key: column.key, direction: rawDirection } satisfies SortConfig;
  }

  return null;
}

function buildSortParam(column: ColumnDefinition, direction: SortDirection): string {
  return `${String(column.key)}:${direction}`;
}

interface FetchState {
  status: "idle" | "loading" | "loaded" | "error";
  items: YardDocument[];
  total: number;
  error?: string;
}

interface FilterFormState {
  transportNumber: string;
  fraction: string;
  sortiment: string;
  carrierName: string;
  supplierName: string;
  statuses: string[];
  timingStatusFilter: string;
  timeWindowFromFrom: string;
  timeWindowFromTo: string;
  timeWindowToFrom: string;
  timeWindowToTo: string;
  createdAtFrom: string;
  createdAtTo: string;
}

interface AppliedFilters {
  transportNumber?: string;
  fractions?: string[];
  sortiment?: string;
  carrierName?: string;
  supplierName?: string;
  statuses?: string[];
  timeWindowFromFrom?: string;
  timeWindowFromTo?: string;
  timeWindowToFrom?: string;
  timeWindowToTo?: string;
  createdAtFrom?: string;
  createdAtTo?: string;
}

type FilterField = keyof FilterFormState;

const PAGE_TEXT = {
  title: "Übersicht der Transporte",
  description: "Listet alle Transporte auf und bietet Schnellaktionen.",
  refresh: "Aktualisieren",
  loading: "Lade Daten …",
  actionInProgress: "Abruf wird ausgeführt …",
  emptyTitle: "Keine Transporte",
  emptyDescription: "Derzeit stehen keine Transporte zur Verfügung.",
  error: "Die Daten konnten nicht geladen werden.",
  filters: {
    title: "Filter",
    apply: "Filter anwenden",
    reset: "Zurücksetzen",
    multiHint: "Mehrere Werte mit Komma trennen",
    fraction: "Fraktion",
    sortiment: "Sortiment",
    carrierName: "Spediteur",
    supplierName: "Lieferant",
    status: "Status",
    timingStatus: "Pünktlichkeit",
    transportNumber: "Transportnummer",
    timeWindowRange: "Zeitfenster (Datum & Uhrzeit)",
    arrivalTimeRange: "Ankunft (Datum & Uhrzeit)"
  },
  variants: {
    title: "Varianten",
    select: "Variante auswählen",
    save: "Aktuelle Ansicht als Variante speichern",
    saveButton: "Speichern",
    cancelButton: "Abbrechen",
    deleteButton: "Löschen",
    namePlaceholder: "Name der Variante",
    setAsDefault: "Als Standard festlegen",
    default: "Standard",
    modeNew: "Neu erstellen",
    modeOverwrite: "Bestehende überschreiben",
    overwriteSelect: "Zu überschreibende Variante auswählen",
    overwritePlaceholder: "Variante auswählen...",
    overwriteRequired: "Bitte wählen Sie eine Variante zum Überschreiben",
    loadSuccess: (name: string) => `Variante "${name}" geladen`,
    saveSuccess: (name: string) => `Variante "${name}" gespeichert`,
    overwriteSuccess: (name: string) => `Variante "${name}" überschrieben`,
    deleteSuccess: (name: string) => `Variante "${name}" gelöscht`,
    loadError: "Fehler beim Laden der Variante",
    saveError: "Fehler beim Speichern der Variante",
    deleteError: "Fehler beim Löschen der Variante",
    nameRequired: "Bitte geben Sie einen Namen ein",
    confirmDelete: (name: string) => `Möchten Sie die Variante "${name}" wirklich löschen?`,
  }
} as const;

function createEmptyFilterFormState(): FilterFormState {
  // Initial: Heute von 00:00 bis 23:59 für Zeitfenster-Filter (lokale Zeit)
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999);

  // Formatierung in lokaler Zeit für datetime-local input (YYYY-MM-DDTHH:mm)
  const formatLocalDateTime = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  return {
    transportNumber: "",
    fraction: "",
    sortiment: "",
    carrierName: "",
    supplierName: "",
    statuses: [],
    timingStatusFilter: "",
    timeWindowFromFrom: formatLocalDateTime(todayStart),
    timeWindowFromTo: formatLocalDateTime(todayEnd),
    timeWindowToFrom: "",
    timeWindowToTo: "",
    createdAtFrom: "",
    createdAtTo: ""
  };
}

function parseMultiValueInput(value: string): string[] {
  return value
    .split(/[\n,]/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

function toISOStringOrNull(value: string): string | null {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed.toISOString();
}

function buildAppliedFilters(form: FilterFormState): AppliedFilters {
  const next: AppliedFilters = {};

  const transportNumber = form.transportNumber.trim();
  if (transportNumber) {
    next.transportNumber = transportNumber;
  }

  const fractions = parseMultiValueInput(form.fraction);
  if (fractions.length > 0) {
    next.fractions = fractions;
  }

  const sortiment = form.sortiment.trim();
  if (sortiment) {
    next.sortiment = sortiment;
  }

  const carrierName = form.carrierName.trim();
  if (carrierName) {
    next.carrierName = carrierName;
  }

  const supplierName = form.supplierName.trim();
  if (supplierName) {
    next.supplierName = supplierName;
  }

  if (form.statuses.length > 0) {
    next.statuses = form.statuses;
  }

  const timeWindowFromFrom = toISOStringOrNull(form.timeWindowFromFrom);
  if (timeWindowFromFrom) {
    next.timeWindowFromFrom = timeWindowFromFrom;
  }

  const timeWindowFromTo = toISOStringOrNull(form.timeWindowFromTo);
  if (timeWindowFromTo) {
    next.timeWindowFromTo = timeWindowFromTo;
  }

  const timeWindowToFrom = toISOStringOrNull(form.timeWindowToFrom);
  if (timeWindowToFrom) {
    next.timeWindowToFrom = timeWindowToFrom;
  }

  const timeWindowToTo = toISOStringOrNull(form.timeWindowToTo);
  if (timeWindowToTo) {
    next.timeWindowToTo = timeWindowToTo;
  }

  const createdAtFrom = toISOStringOrNull(form.createdAtFrom);
  if (createdAtFrom) {
    next.createdAtFrom = createdAtFrom;
  }

  const createdAtTo = toISOStringOrNull(form.createdAtTo);
  if (createdAtTo) {
    next.createdAtTo = createdAtTo;
  }

  return next;
}

// Helper: Konvertiere FilterFormState zu YardVariantFilters (ohne Datumsfilter)
function filterFormToVariantFilters(form: FilterFormState): YardVariantFilters {
  const filters: YardVariantFilters = {};

  const transportNumber = form.transportNumber.trim();
  if (transportNumber) {
    filters.transportNumber = transportNumber;
  }

  const fractions = parseMultiValueInput(form.fraction);
  if (fractions.length > 0) {
    filters.fractions = fractions;
  }

  const carrierName = form.carrierName.trim();
  if (carrierName) {
    filters.carrierName = carrierName;
  }

  const supplierName = form.supplierName.trim();
  if (supplierName) {
    filters.supplierName = supplierName;
  }

  if (form.statuses.length > 0) {
    filters.statuses = form.statuses;
  }

  return filters;
}

// Helper: Konvertiere YardVariantFilters zu FilterFormState (mit leeren Datumsfeldern)
function variantFiltersToFilterForm(filters: YardVariantFilters): Partial<FilterFormState> {
  return {
    transportNumber: filters.transportNumber ?? "",
    fraction: filters.fractions?.join(", ") ?? "",
    sortiment: "",
    carrierName: filters.carrierName ?? "",
    supplierName: filters.supplierName ?? "",
    statuses: filters.statuses ?? [],
  };
}

// Helper: Konvertiere SortConfig zu YardVariantSort
function sortConfigToVariantSort(sortConfig: SortConfig | null): YardVariantSort | null {
  if (!sortConfig) {
    return null;
  }

  return {
    key: String(sortConfig.key),
    direction: sortConfig.direction,
  };
}

// Helper: Konvertiere YardVariantSort zu SortConfig
function variantSortToSortConfig(sort: YardVariantSort | null): SortConfig | null {
  if (!sort) {
    return null;
  }

  return {
    key: sort.key as ColumnDefinition["key"],
    direction: sort.direction,
  };
}

export function YardListPage() {
  const [state, setState] = useState<FetchState>({ status: "idle", items: [], total: 0 });
  const [documents, setDocuments] = useState<YardDocument[]>([]);
  const [filters, setFilters] = useState<FilterFormState>(() => createEmptyFilterFormState());
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>(() => buildAppliedFilters(createEmptyFilterFormState()));
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(() => parseSortParam(DEFAULT_SORT_PARAM));
  const [sortParam, setSortParam] = useState<string | null>(DEFAULT_SORT_PARAM);
  const [actionMenuRowId, setActionMenuRowId] = useState<string | null>(null);
  const [pendingActionRowId, setPendingActionRowId] = useState<string | null>(null);
  const [actionFeedback, setActionFeedback] = useState<string | null>(null);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState<boolean>(false);
  const language = FALLBACK_LANGUAGE;
  const actionTimeoutRef = useRef<number | null>(null);
  const feedbackTimeoutRef = useRef<number | null>(null);
  const weightConfirmationPollRef = useRef<number | null>(null);
  const weightConfirmationFetchInFlightRef = useRef(false);
  const [weightConfirmations, setWeightConfirmations] = useState<WeightConfirmationItem[]>([]);
  const [activeWeightConfirmationId, setActiveWeightConfirmationId] = useState<string | null>(null);
  const [confirmingWeightConfirmationId, setConfirmingWeightConfirmationId] = useState<string | null>(null);
  const [weightConfirmationActionError, setWeightConfirmationActionError] = useState<string | null>(null);
  const [weightConfirmationFetchError, setWeightConfirmationFetchError] = useState<string | null>(null);
  const [isEditingWeight, setIsEditingWeight] = useState<boolean>(false);
  const [editedWeight, setEditedWeight] = useState<string>("");

  // Chat-Modus: Verhindert automatisches fetchDocuments wenn Chat-Dokumente geladen wurden
  const [isChatMode, setIsChatMode] = useState<boolean>(false);

  // View-Modus: Tabelle oder Kalender
  const [viewMode, setViewMode] = useState<"table" | "calendar">("table");

  // Varianten-State
  const [variants, setVariants] = useState<TableVariant[]>([]);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [showVariantDialog, setShowVariantDialog] = useState<boolean>(false);
  const [variantDialogMode, setVariantDialogMode] = useState<"new" | "overwrite">("new");
  const [variantNameInput, setVariantNameInput] = useState<string>("");
  const [variantOverwriteId, setVariantOverwriteId] = useState<string>("");
  const [variantIsDefaultInput, setVariantIsDefaultInput] = useState<boolean>(false);
  const [variantDialogError, setVariantDialogError] = useState<string | null>(null);
  const [isSavingVariant, setIsSavingVariant] = useState<boolean>(false);
  const defaultVariantLoadedRef = useRef<boolean>(false);
  const [isFilterCollapsed, setIsFilterCollapsed] = useState<boolean>(true);
  const [isSummaryCollapsed, setIsSummaryCollapsed] = useState<boolean>(false);

  const fetchDocuments = useCallback(async () => {
    setActionMenuRowId(null);
    setPendingActionRowId(null);
    setState((prev) => ({ ...prev, status: "loading", error: undefined }));

    try {
      // Direkt MongoDB Query über manuelle GET Route
      const params = new URLSearchParams();
      params.set("language", language);
      const trimmedSort = sortParam?.trim();
      if (trimmedSort) {
        params.set("sort", trimmedSort);
      }

      appendFiltersToSearchParams(params, appliedFilters);

      const url = `${API_BASE_URL}/api/yard-documents?${params.toString()}`;

      const response = await fetch(url, {
        credentials: "include",
        headers: {
          ...getAuthHeaders(),
          "Accept-Language": language
        }
      });


      if (response.status === 401) {
        const message = await extractErrorMessage(response);
        throw new Error(message ?? "Authentifizierung erforderlich. Bitte melde dich erneut an.");
      }

      if (!response.ok) {
        const message = await extractErrorMessage(response);
        throw new Error(message ?? `Datenabruf fehlgeschlagen (${response.status})`);
      }

      const payload = (await response.json()) as YardDocumentListResponse;
      const items = Array.isArray(payload.items) ? payload.items : [];
      const total = Number.isFinite(payload.total) ? payload.total : items.length;

      setState({ status: "loaded", items, total });
      setDocuments(items);
    } catch (error) {
      const message = error instanceof Error ? error.message : PAGE_TEXT.error;
      setState((prev) => ({ ...prev, status: "error", error: message }));
      setDocuments((prev) => (prev.length > 0 ? prev : []));
    }
  }, [appliedFilters, language, sortParam]);

  const loadWeightConfirmations = useCallback(async () => {
    if (weightConfirmationFetchInFlightRef.current) {
      return;
    }

    weightConfirmationFetchInFlightRef.current = true;

    try {
      const params = new URLSearchParams();
      params.set("limit", String(WEIGHT_CONFIRMATION_FETCH_LIMIT));
      const url = `${API_BASE_URL}/api/yard/weight-confirmations/pending?${params.toString()}`;

      const response = await fetch(url, {
        headers: getAuthHeaders(),
        credentials: "include"
      });

      if (!response.ok) {
        const message = await extractErrorMessage(response);
        console.error("[YardList] Fetch failed:", message, "Status:", response.status);
        throw new Error(message || WEIGHT_CONFIRMATION_TEXT.fetchError);
      }

      const payload = (await response.json()) as { items?: WeightConfirmationItem[] | null } | null;

      const items = Array.isArray(payload?.items) ? (payload?.items as WeightConfirmationItem[]) : [];

      const pendingItems = items
        .filter((item) => item.status === "pending")
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

      setWeightConfirmations(pendingItems);
      setActiveWeightConfirmationId((current) => {
        if (current && pendingItems.some((item) => item.id === current)) {
          return current;
        }

        return pendingItems.length > 0 ? pendingItems[0].id : null;
      });
      setWeightConfirmationFetchError(null);
    } catch (error) {
      const message = error instanceof Error && error.message ? error.message : WEIGHT_CONFIRMATION_TEXT.fetchError;
      setWeightConfirmationFetchError(message);
    } finally {
      weightConfirmationFetchInFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    // CRITICAL: Nur fetchDocuments aufrufen wenn NICHT im Chat-Modus
    // Chat-Modus bedeutet: Dokumente wurden via Chat geladen und sollen nicht überschrieben werden
    if (!isChatMode) {
      void fetchDocuments();
    }
  }, [fetchDocuments, isChatMode]);

  // Varianten beim Laden holen
  useEffect(() => {
    const loadVariants = async () => {
      try {
        console.log('[YardList] Loading variants for applicationId:', APPLICATION_ID);
        const loadedVariants = await listTableVariants(APPLICATION_ID);
        console.log('[YardList] Loaded variants:', loadedVariants);
        // API gibt { data: [...], pagination: {...} } zurück, extrahiere das data Array
        const variantsArray = Array.isArray(loadedVariants)
          ? loadedVariants
          : (loadedVariants as any)?.data || [];
        console.log('[YardList] Setting variants array:', variantsArray);
        setVariants(variantsArray);
      } catch (error) {
        console.error("Failed to load variants:", error);
      }
    };

    void loadVariants();
  }, []);

  // Default-Variante beim ersten Laden anwenden
  useEffect(() => {
    if (defaultVariantLoadedRef.current) {
      return;
    }

    const loadDefaultVariant = async () => {
      try {
        const defaultVariant = await getDefaultTableVariant(APPLICATION_ID);
        if (defaultVariant) {
          // Use applyVariant to avoid duplication and ensure consistent behavior
          applyVariant(defaultVariant);
          defaultVariantLoadedRef.current = true;
        }
      } catch (error) {
        console.error("Failed to load default variant:", error);
      }
    };

    void loadDefaultVariant();
  }, []);

  useEffect(() => {
    void loadWeightConfirmations();

    if (weightConfirmationPollRef.current != null) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void loadWeightConfirmations();
    }, WEIGHT_CONFIRMATION_POLL_INTERVAL_MS);

    weightConfirmationPollRef.current = intervalId;

    return () => {
      window.clearInterval(intervalId);
      weightConfirmationPollRef.current = null;
    };
  }, [loadWeightConfirmations]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (statusDropdownOpen && !target.closest('[data-dropdown="status"]')) {
        setStatusDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [statusDropdownOpen]);

  useEffect(() => {
    return () => {
      if (actionTimeoutRef.current) {
        window.clearTimeout(actionTimeoutRef.current);
      }

      if (feedbackTimeoutRef.current) {
        window.clearTimeout(feedbackTimeoutRef.current);
      }

      if (weightConfirmationPollRef.current) {
        window.clearInterval(weightConfirmationPollRef.current);
        weightConfirmationPollRef.current = null;
      }
    };
  }, []);

  // Schließe Action-Menü bei Klick außerhalb
  useEffect(() => {
    if (!actionMenuRowId) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Prüfe ob der Klick innerhalb eines Action-Menüs oder Action-Buttons war
      if (
        target.closest('.yard-list-action-menu') ||
        target.closest('.yard-list-action-button')
      ) {
        return;
      }

      // Schließe das Menü
      setActionMenuRowId(null);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [actionMenuRowId]);

  const handleRefresh = useCallback(() => {
    if (pendingActionRowId) {
      return;
    }

    // Im Chat-Modus: NUR Weight Confirmations aktualisieren, NICHT die Dokumente
    // (Chat-Dokumente kommen vom Chat-Backend und sollten nicht überschrieben werden)
    if (isChatMode) {
      void loadWeightConfirmations();
      return;
    }

    // Normal-Modus: Dokumente UND Weight Confirmations neu laden
    void fetchDocuments();
    void loadWeightConfirmations();
  }, [fetchDocuments, loadWeightConfirmations, pendingActionRowId, isChatMode]);

  const closeActionMenu = useCallback(() => {
    setActionMenuRowId(null);
  }, []);

  const handleFilterChange = useCallback(
    (field: FilterField) => (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setFilters((current) => ({ ...current, [field]: value }));
    },
    []
  );

  const handleFilterSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      // CRITICAL: Chat-Modus deaktivieren wenn normale Filter angewendet werden
      setIsChatMode(false);
      const nextApplied = buildAppliedFilters(filters);
      setActionMenuRowId(null);
      if (pendingActionRowId) {
        setPendingActionRowId(null);
      }
      setAppliedFilters(nextApplied);
    },
    [filters, pendingActionRowId]
  );

  const handleFilterReset = useCallback(() => {
    // CRITICAL: Chat-Modus deaktivieren wenn Filter zurückgesetzt werden
    setIsChatMode(false);
    setFilters(createEmptyFilterFormState());
    setAppliedFilters({});
    setActionMenuRowId(null);
    setSelectedVariantId(null); // Varianten-Auswahl zurücksetzen
    if (pendingActionRowId) {
      setPendingActionRowId(null);
    }
  }, [pendingActionRowId]);

  // Varianten-Handler
  const applyVariant = useCallback((variant: TableVariant) => {
    // CRITICAL: Chat-Modus deaktivieren wenn Variante geladen wird
    setIsChatMode(false);

    // Filter aus Variante anwenden (ohne Datumsfilter)
    const variantFilters = (variant.configuration?.filters ?? {}) as YardVariantFilters;
    const partialFilters = variantFiltersToFilterForm(variantFilters);
    const emptyForm = createEmptyFilterFormState();

    const newFilters: FilterFormState = {
      ...emptyForm,
      ...partialFilters,
    };

    setFilters(newFilters);
    setAppliedFilters(buildAppliedFilters(newFilters));

    // Sortierung anwenden
    const variantSort = variant.configuration?.sort as YardVariantSort | null | undefined;
    if (variantSort) {
      const newSortConfig = variantSortToSortConfig(variantSort);
      setSortConfig(newSortConfig);
      if (newSortConfig) {
        const column = COLUMNS.find((c) => c.key === newSortConfig.key);
        if (column) {
          setSortParam(buildSortParam(column, newSortConfig.direction));
        }
      }
    } else {
      setSortConfig(null);
      setSortParam(null);
    }
  }, []);

  const handleVariantSelect = useCallback(async (variantId: string) => {
    if (!variantId) {
      setSelectedVariantId(null);
      return;
    }

    const variant = variants.find((v) => v._id === variantId);
    if (!variant) {
      return;
    }

    try {
      applyVariant(variant);
      setSelectedVariantId(variantId);
      showFeedbackMessage(PAGE_TEXT.variants.loadSuccess(variant.name));
    } catch (error) {
      console.error("Failed to apply variant:", error);
      showFeedbackMessage(PAGE_TEXT.variants.loadError);
    }
  }, [variants, applyVariant]);

  const handleOpenSaveVariantDialog = useCallback(() => {
    setShowVariantDialog(true);
    setVariantDialogMode("new");
    setVariantNameInput("");
    setVariantOverwriteId("");
    setVariantIsDefaultInput(false);
    setVariantDialogError(null);
  }, []);

  const handleCloseVariantDialog = useCallback(() => {
    setShowVariantDialog(false);
    setVariantDialogMode("new");
    setVariantNameInput("");
    setVariantOverwriteId("");
    setVariantIsDefaultInput(false);
    setVariantDialogError(null);
  }, []);

  const handleSaveVariant = useCallback(async () => {
    // Validierung je nach Modus
    if (variantDialogMode === "new") {
      const trimmedName = variantNameInput.trim();
      if (!trimmedName) {
        setVariantDialogError(PAGE_TEXT.variants.nameRequired);
        return;
      }
    } else {
      // Überschreiben-Modus
      if (!variantOverwriteId) {
        setVariantDialogError(PAGE_TEXT.variants.overwriteRequired);
        return;
      }
    }

    setIsSavingVariant(true);
    setVariantDialogError(null);

    try {
      const variantFilters = filterFormToVariantFilters(filters);
      const variantSort = sortConfig ? sortConfigToVariantSort(sortConfig) : null;

      if (variantDialogMode === "new") {
        // Neu erstellen
        const trimmedName = variantNameInput.trim();
        const newVariant = await createTableVariant({
          applicationId: APPLICATION_ID,
          name: trimmedName,
          isDefault: variantIsDefaultInput,
          configuration: {
            filters: variantFilters,
            sort: variantSort,
          },
        });

        setVariants((prev) => [...(Array.isArray(prev) ? prev : []), newVariant]);
        setSelectedVariantId(newVariant._id);
        handleCloseVariantDialog();
        showFeedbackMessage(PAGE_TEXT.variants.saveSuccess(trimmedName));
      } else {
        // Bestehende überschreiben
        const variantToOverwrite = variants.find((v) => v._id === variantOverwriteId);
        if (!variantToOverwrite) {
          setVariantDialogError("Variante nicht gefunden");
          return;
        }

        const updatedVariant = await updateTableVariant(variantOverwriteId, {
          name: variantToOverwrite.name, // Name bleibt gleich
          isDefault: variantIsDefaultInput,
          configuration: {
            filters: variantFilters,
            sort: variantSort,
          },
        });

        setVariants((prev) => (Array.isArray(prev) ? prev : []).map((v) => (v._id === variantOverwriteId ? updatedVariant : v)));
        setSelectedVariantId(updatedVariant._id);
        handleCloseVariantDialog();
        showFeedbackMessage(PAGE_TEXT.variants.overwriteSuccess(variantToOverwrite.name));
      }
    } catch (error) {
      console.error("Failed to save variant:", error);
      setVariantDialogError(PAGE_TEXT.variants.saveError);
    } finally {
      setIsSavingVariant(false);
    }
  }, [variantDialogMode, variantNameInput, variantOverwriteId, variantIsDefaultInput, filters, sortConfig, variants, handleCloseVariantDialog]);

  const handleDeleteVariant = useCallback(async (variantId: string) => {
    const variant = variants.find((v) => v._id === variantId);
    if (!variant) {
      return;
    }

    if (!confirm(PAGE_TEXT.variants.confirmDelete(variant.name))) {
      return;
    }

    try {
      await deleteTableVariant(variantId);
      setVariants((prev) => prev.filter((v) => v._id !== variantId));

      if (selectedVariantId === variantId) {
        setSelectedVariantId(null);
      }

      showFeedbackMessage(PAGE_TEXT.variants.deleteSuccess(variant.name));
    } catch (error) {
      console.error("Failed to delete variant:", error);
      showFeedbackMessage(PAGE_TEXT.variants.deleteError);
    }
  }, [variants, selectedVariantId]);

  const toggleFilterCollapse = useCallback(() => {
    setIsFilterCollapsed((prev) => !prev);
  }, []);

  const toggleSummaryCollapse = useCallback(() => {
    setIsSummaryCollapsed((prev) => !prev);
  }, []);

  const showFeedbackMessage = useCallback((message: string | null) => {
    if (feedbackTimeoutRef.current) {
      window.clearTimeout(feedbackTimeoutRef.current);
      feedbackTimeoutRef.current = null;
    }

    setActionFeedback(message);

    if (message) {
      const timeoutId = window.setTimeout(() => {
        setActionFeedback(null);
        feedbackTimeoutRef.current = null;
      }, 2000); // 2 Sekunden wie gewünscht

      feedbackTimeoutRef.current = timeoutId;
    }
  }, []);

  const toggleActionMenu = useCallback(
    (rowId: string) => {
      if (pendingActionRowId) {
        return;
      }

      setActionMenuRowId((current) => (current === rowId ? null : rowId));
    },
    [pendingActionRowId]
  );

  const toggleSort = useCallback((column: ColumnDefinition) => {
    closeActionMenu();

    if (pendingActionRowId || column.type === "status" || column.sortable === false) {
      return;
    }

    if (!sortConfig || sortConfig.key !== column.key) {
      const nextConfig: SortConfig = { key: column.key, direction: "asc" };
      setSortConfig(nextConfig);
      setSortParam(buildSortParam(column, "asc"));
      return;
    }

    if (sortConfig.direction === "asc") {
      const nextConfig: SortConfig = { key: column.key, direction: "desc" };
      setSortConfig(nextConfig);
      setSortParam(buildSortParam(column, "desc"));
      return;
    }

    setSortConfig(null);
    setSortParam(null);
  }, [closeActionMenu, pendingActionRowId, sortConfig]);

  const handleAbruf = useCallback(
    async (document: YardDocument) => {
      if (pendingActionRowId) {
        return;
      }

      const recordId = typeof document._id === "string" ? document._id : null;
      if (!recordId) {
        return;
      }

      closeActionMenu();
      setPendingActionRowId(recordId);
      setActionFeedback(null);

      if (actionTimeoutRef.current) {
        window.clearTimeout(actionTimeoutRef.current);
      }

      try {
        // API-Call zum Aktualisieren des Status auf "2" (Abruf erfolgt) und calledAt setzen
        const updateUrl = `${API_BASE_URL}/api/yard-documents/${recordId}`;
        const response = await fetch(updateUrl, {
          method: "PUT",
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
            "Accept-Language": language
          },
          credentials: "include",
          body: JSON.stringify({
            LKW_LOAD_STATUS: "2",
            calledAt: new Date().toISOString()
          })
        });

        if (!response.ok) {
          throw new Error(`Status-Update fehlgeschlagen: ${response.status}`);
        }

        const updated = await response.json();

        // UI aktualisieren mit dem aktualisierten Dokument
        setDocuments((prev) =>
          prev.map((entry) => {
            if (entry._id === recordId) {
              return {
                ...entry,
                LKW_LOAD_STATUS: updated.LKW_LOAD_STATUS,
                calledAt: updated.calledAt
              };
            }
            return entry;
          })
        );

        const batchLabel =
          typeof document.TRANSPORTCHARGE === "string" && document.TRANSPORTCHARGE.trim().length > 0
            ? document.TRANSPORTCHARGE.trim()
            : typeof document.transportNumber === "string" && document.transportNumber.trim().length > 0
              ? document.transportNumber.trim()
              : "Dokument";
        showFeedbackMessage(`Abruf für ${batchLabel} erfolgreich abgeschlossen.`);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Unbekannter Fehler";
        showFeedbackMessage(`Fehler beim Abruf: ${errorMessage}`);
      } finally {
        setPendingActionRowId(null);
      }
    },
    [closeActionMenu, pendingActionRowId, showFeedbackMessage, language]
  );

  const activeWeightConfirmation = useMemo(() => {
    if (weightConfirmations.length === 0) {
      return null;
    }

    if (activeWeightConfirmationId) {
      const match = weightConfirmations.find((item) => item.id === activeWeightConfirmationId);
      if (match) {
        return match;
      }
    }

    return weightConfirmations[0] ?? null;
  }, [activeWeightConfirmationId, weightConfirmations]);

  const queueMessage = useMemo(() => {
    if (!activeWeightConfirmation) {
      return null;
    }

    const remaining = weightConfirmations.filter((item) => item.id !== activeWeightConfirmation.id).length;
    if (remaining === 0) {
      return null;
    }

    return remaining === 1
      ? WEIGHT_CONFIRMATION_TEXT.queueHintSingle
      : WEIGHT_CONFIRMATION_TEXT.queueHintPlural(remaining);
  }, [activeWeightConfirmation, weightConfirmations]);

  const formattedWeight = useMemo(() => {
    if (!activeWeightConfirmation) {
      return null;
    }

    return new Intl.NumberFormat(language, {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    }).format(activeWeightConfirmation.weight);
  }, [activeWeightConfirmation, language]);

  const formattedCreatedAt = useMemo(() => {
    if (!activeWeightConfirmation) {
      return "–";
    }

    const value = formatResultValue(activeWeightConfirmation.createdAt, language, DATETIME_FORMAT);
    return typeof value === "string" && value.trim().length > 0 ? value : "–";
  }, [activeWeightConfirmation, language]);

  const isConfirmingWeight = confirmingWeightConfirmationId != null;
  const isConfirmingActive = Boolean(
    activeWeightConfirmation && confirmingWeightConfirmationId === activeWeightConfirmation.id
  );

  useEffect(() => {
    if (!activeWeightConfirmation) {
      setWeightConfirmationActionError(null);
      setIsEditingWeight(false);
      setEditedWeight("");
    }
  }, [activeWeightConfirmation]);

  const handleConfirmWeight = useCallback(async () => {
    const currentItem = activeWeightConfirmation;

    if (!currentItem || isConfirmingWeight) {
      return;
    }

    setWeightConfirmationActionError(null);
    setConfirmingWeightConfirmationId(currentItem.id);

    try {
      const updatedWeight = isEditingWeight && editedWeight.trim() !== ""
        ? parseFloat(editedWeight)
        : null;

      const body = updatedWeight != null && updatedWeight > 0 ? { weight: updatedWeight } : {};

      const response = await fetch(
        `${API_BASE_URL}/api/yard/weight-confirmations/${encodeURIComponent(currentItem.id)}/confirm`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json"
          },
          body: JSON.stringify(body)
        }
      );

      if (response.status === 404) {
        setWeightConfirmations((prev) => {
          const next = prev.filter((item) => item.id !== currentItem.id);
          setActiveWeightConfirmationId((activeId) => {
            if (activeId && activeId !== currentItem.id && next.some((item) => item.id === activeId)) {
              return activeId;
            }

            return next.length > 0 ? next[0].id : null;
          });
          return next;
        });
        setWeightConfirmationFetchError(null);
        setWeightConfirmationActionError(WEIGHT_CONFIRMATION_TEXT.missingError);
        await loadWeightConfirmations();
        return;
      }

      if (!response.ok) {
        const message = await extractErrorMessage(response);
        throw new Error(message || WEIGHT_CONFIRMATION_TEXT.defaultError);
      }

      const payload = (await response.json()) as WeightConfirmationItem;
      setWeightConfirmations((prev) => {
        const next = prev.filter((item) => item.id !== currentItem.id);
        setActiveWeightConfirmationId((activeId) => {
          if (activeId && activeId !== currentItem.id && next.some((item) => item.id === activeId)) {
            return activeId;
          }

          return next.length > 0 ? next[0].id : null;
        });
        return next;
      });
      setWeightConfirmationActionError(null);
      setWeightConfirmationFetchError(null);
      setIsEditingWeight(false);
      setEditedWeight("");
      showFeedbackMessage(`Gewicht für ${payload.transportBatchNumber} bestätigt.`);
      await loadWeightConfirmations();
    } catch (error) {
      const message = error instanceof Error && error.message ? error.message : WEIGHT_CONFIRMATION_TEXT.defaultError;
      setWeightConfirmationActionError(message);
    } finally {
      setConfirmingWeightConfirmationId(null);
    }
  }, [activeWeightConfirmation, isConfirmingWeight, isEditingWeight, editedWeight, loadWeightConfirmations, showFeedbackMessage]);

  const handleEditWeight = useCallback(() => {
    if (!activeWeightConfirmation || isConfirmingWeight) {
      return;
    }
    setIsEditingWeight(true);
    setEditedWeight(String(activeWeightConfirmation.weight));
    setWeightConfirmationActionError(null);
  }, [activeWeightConfirmation, isConfirmingWeight]);

  const handleCancelEditWeight = useCallback(() => {
    setIsEditingWeight(false);
    setEditedWeight("");
    setWeightConfirmationActionError(null);
  }, []);

  const handleWeightInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Nur positive Zahlen erlauben
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setEditedWeight(value);
    }
  }, []);

  const isInitialLoading = state.status === "loading" && state.items.length === 0;
  const showEmptyState = state.status === "loaded" && state.items.length === 0;

  // Clientseitige Filterung für timingStatus
  const baseItems = state.status === "loaded" ? (documents.length > 0 ? documents : state.items) : state.items;
  const visibleItems = useMemo(() => {
    if (!filters.timingStatusFilter) {
      return baseItems;
    }
    return baseItems.filter(item => {
      const timingStatus = item.timingStatus as { state?: string } | null | undefined;
      return timingStatus?.state === filters.timingStatusFilter;
    });
  }, [baseItems, filters.timingStatusFilter]);

  // Material Summary Calculation
  const materialSummary = useMemo(() => calculateMaterialSummary(baseItems), [baseItems]);

  const hasActiveFilters = useMemo(() => Object.keys(appliedFilters).length > 0, [appliedFilters]);
  const isFilterFormPristine = useMemo(() => isFilterFormEmpty(filters), [filters]);

  // Handler für KI-Chat-Filter
  const handleChatFilterApply = useCallback((chatFilter: Record<string, unknown>) => {
    // Wenn leerer Filter → alle Filter zurücksetzen (alle Dokumente anzeigen)
    if (Object.keys(chatFilter).length === 0) {
      const emptyFilters = createEmptyFilterFormState();
      setFilters(emptyFilters);
      setAppliedFilters({});
      return;
    }

    const newFilters: Partial<FilterFormState> = {};

    // transportNumber
    if (chatFilter.transportNumber && typeof chatFilter.transportNumber === "string") {
      newFilters.transportNumber = chatFilter.transportNumber;
    }

    // status (einzeln oder Array)
    if (chatFilter.status !== undefined) {
      const statuses = Array.isArray(chatFilter.status)
        ? chatFilter.status.map(String)
        : [String(chatFilter.status)];
      if (statuses.length > 0) {
        newFilters.statuses = statuses;
      }
    }

    // fraction (einzeln oder Array)
    if (chatFilter.fraction !== undefined) {
      const fractions = Array.isArray(chatFilter.fraction)
        ? chatFilter.fraction
        : [String(chatFilter.fraction)];
      newFilters.fraction = fractions.join(", ");
    }

    // carrierName
    if (chatFilter.carrierName && typeof chatFilter.carrierName === "string") {
      newFilters.carrierName = chatFilter.carrierName;
    }

    // supplierName
    if (chatFilter.supplierName && typeof chatFilter.supplierName === "string") {
      newFilters.supplierName = chatFilter.supplierName;
    }

    // Datum-Filter (createdAt mit $gte/$lte)
    if (chatFilter.createdAt && typeof chatFilter.createdAt === "object") {
      const dateRange = chatFilter.createdAt as { $gte?: string; $lte?: string };
      if (dateRange.$gte) {
        const parsed = new Date(dateRange.$gte);
        if (!Number.isNaN(parsed.getTime())) {
          const year = parsed.getFullYear();
          const month = String(parsed.getMonth() + 1).padStart(2, "0");
          const day = String(parsed.getDate()).padStart(2, "0");
          const hours = String(parsed.getHours()).padStart(2, "0");
          const minutes = String(parsed.getMinutes()).padStart(2, "0");
          newFilters.createdAtFrom = `${year}-${month}-${day}T${hours}:${minutes}`;
        }
      }
      if (dateRange.$lte) {
        const parsed = new Date(dateRange.$lte);
        if (!Number.isNaN(parsed.getTime())) {
          const year = parsed.getFullYear();
          const month = String(parsed.getMonth() + 1).padStart(2, "0");
          const day = String(parsed.getDate()).padStart(2, "0");
          const hours = String(parsed.getHours()).padStart(2, "0");
          const minutes = String(parsed.getMinutes()).padStart(2, "0");
          newFilters.createdAtTo = `${year}-${month}-${day}T${hours}:${minutes}`;
        }
      }
    }

    // Zeitfenster-Filter (timeWindowFrom mit $gte/$lte)
    if (chatFilter.timeWindowFrom && typeof chatFilter.timeWindowFrom === "object") {
      const dateRange = chatFilter.timeWindowFrom as { $gte?: string; $lte?: string };
      if (dateRange.$gte) {
        const parsed = new Date(dateRange.$gte);
        if (!Number.isNaN(parsed.getTime())) {
          const year = parsed.getFullYear();
          const month = String(parsed.getMonth() + 1).padStart(2, "0");
          const day = String(parsed.getDate()).padStart(2, "0");
          const hours = String(parsed.getHours()).padStart(2, "0");
          const minutes = String(parsed.getMinutes()).padStart(2, "0");
          newFilters.timeWindowFromFrom = `${year}-${month}-${day}T${hours}:${minutes}`;
        }
      }
      if (dateRange.$lte) {
        const parsed = new Date(dateRange.$lte);
        if (!Number.isNaN(parsed.getTime())) {
          const year = parsed.getFullYear();
          const month = String(parsed.getMonth() + 1).padStart(2, "0");
          const day = String(parsed.getDate()).padStart(2, "0");
          const hours = String(parsed.getHours()).padStart(2, "0");
          const minutes = String(parsed.getMinutes()).padStart(2, "0");
          newFilters.timeWindowFromTo = `${year}-${month}-${day}T${hours}:${minutes}`;
        }
      }
    }

    // Filter überschreiben und anwenden
    setFilters((prev) => ({ ...prev, ...newFilters }));
    const applied = buildAppliedFilters({ ...filters, ...newFilters });
    setAppliedFilters(applied);
  }, [filters]);

  // Handler für direkte Dokumenten-Ladung via Chat
  const handleChatDocumentsLoad = useCallback((documents: Array<Record<string, unknown>>) => {
    // CRITICAL: Chat-Modus aktivieren um fetchDocuments zu verhindern
    setIsChatMode(true);

    // WICHTIG: Filter NICHT zurücksetzen - sie wurden vom Chat gesetzt
    // und sollen beim Refresh beibehalten werden

    // Dokumente direkt setzen
    setDocuments(documents as YardDocument[]);
    setState({
      status: "loaded",
      items: documents as YardDocument[],
      total: documents.length
    });

    console.log(`[YardListPage] Chat-Modus aktiviert: ${documents.length} Dokumente geladen`);
  }, []);



  return (
    <div className="yard-list-container">
      {actionFeedback && (
        <div className="yard-list-notification success" role="status" aria-live="polite">
          {actionFeedback}
        </div>
      )}

      <main
        className="yard-list-shell"
        aria-busy={pendingActionRowId ? true : undefined}
        aria-hidden={activeWeightConfirmation ? true : undefined}
      >

        {/* Varianten-Dialog */}
        {showVariantDialog && (
          <div className="yard-variant-dialog-overlay" onClick={handleCloseVariantDialog}>
            <form
              className="yard-variant-dialog"
              onClick={(e) => e.stopPropagation()}
              onSubmit={(e) => {
                e.preventDefault();
                void handleSaveVariant();
              }}
            >
              <header className="yard-variant-dialog-header">
                <h3>{PAGE_TEXT.variants.save}</h3>
              </header>
              <div className="yard-variant-dialog-body">
                {variantDialogError && (
                  <div className="yard-variant-dialog-error" role="alert">
                    {variantDialogError}
                  </div>
                )}

                {/* Modus-Auswahl */}
                <div className="yard-variant-dialog-field">
                  <div className="yard-variant-dialog-mode-toggle">
                    <label className="yard-variant-dialog-mode-option">
                      <input
                        type="radio"
                        name="variant-mode"
                        value="new"
                        checked={variantDialogMode === "new"}
                        onChange={() => {
                          setVariantDialogMode("new");
                          setVariantDialogError(null);
                        }}
                        disabled={isSavingVariant}
                      />
                      <span>{PAGE_TEXT.variants.modeNew}</span>
                    </label>
                    <label className="yard-variant-dialog-mode-option">
                      <input
                        type="radio"
                        name="variant-mode"
                        value="overwrite"
                        checked={variantDialogMode === "overwrite"}
                        onChange={() => {
                          setVariantDialogMode("overwrite");
                          setVariantDialogError(null);
                        }}
                        disabled={isSavingVariant || variants.length === 0}
                      />
                      <span>{PAGE_TEXT.variants.modeOverwrite}</span>
                    </label>
                  </div>
                </div>

                {/* Eingabefelder je nach Modus */}
                {variantDialogMode === "new" ? (
                  <div className="yard-variant-dialog-field">
                    <label htmlFor="variant-name" className="yard-variant-dialog-label">
                      Name:
                    </label>
                    <input
                      id="variant-name"
                      type="text"
                      className="yard-variant-dialog-input"
                      value={variantNameInput}
                      onChange={(e) => setVariantNameInput(e.target.value)}
                      placeholder={PAGE_TEXT.variants.namePlaceholder}
                      autoFocus
                      disabled={isSavingVariant}
                    />
                  </div>
                ) : (
                  <div className="yard-variant-dialog-field">
                    <label htmlFor="variant-overwrite" className="yard-variant-dialog-label">
                      {PAGE_TEXT.variants.overwriteSelect}:
                    </label>
                    <select
                      id="variant-overwrite"
                      className="yard-variant-dialog-input"
                      value={variantOverwriteId}
                      onChange={(e) => setVariantOverwriteId(e.target.value)}
                      disabled={isSavingVariant}
                      autoFocus
                    >
                      <option value="">{PAGE_TEXT.variants.overwritePlaceholder}</option>
                      {variants.map((variant) => (
                        <option key={variant._id} value={variant._id}>
                          {variant.name} {variant.isDefault ? `(${PAGE_TEXT.variants.default})` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="yard-variant-dialog-field">
                  <label className="yard-variant-dialog-checkbox-label">
                    <input
                      type="checkbox"
                      checked={variantIsDefaultInput}
                      onChange={(e) => setVariantIsDefaultInput(e.target.checked)}
                      disabled={isSavingVariant}
                    />
                    <span>{PAGE_TEXT.variants.setAsDefault}</span>
                  </label>
                </div>
              </div>
              <footer className="yard-variant-dialog-footer">
                <button
                  type="button"
                  className="yard-variant-dialog-button yard-variant-dialog-button-secondary"
                  onClick={handleCloseVariantDialog}
                  disabled={isSavingVariant}
                >
                  {PAGE_TEXT.variants.cancelButton}
                </button>
                <button
                  type="submit"
                  className="yard-variant-dialog-button yard-variant-dialog-button-primary"
                  disabled={isSavingVariant}
                >
                  {isSavingVariant ? "..." : PAGE_TEXT.variants.saveButton}
                </button>
              </footer>
            </form>
          </div>
        )}

        <section className="yard-list-content">
          {viewMode === "calendar" ? (
            <YardCalendarView
              documents={documents}
              onDocumentClick={() => { }}
              onBackToTable={() => setViewMode("table")}
            />
          ) : (
            <>
              {/* Material Overview Summary Table */}
              <section className={`yard-material-summary ${isSummaryCollapsed ? 'yard-material-summary--collapsed' : ''}`} aria-label="Material Übersicht">
                <header className="yard-material-summary-header">
                  <h2 className="yard-material-summary-title">Übersicht Transporte auf dem Hof</h2>
                  <button
                    type="button"
                    className="yard-material-summary-toggle"
                    onClick={toggleSummaryCollapse}
                    aria-expanded={!isSummaryCollapsed}
                    aria-label={isSummaryCollapsed ? "Übersicht einblenden" : "Übersicht ausblenden"}
                  >
                    <span className="yard-material-summary-toggle-icon" aria-hidden="true">
                      {isSummaryCollapsed ? "▼" : "▲"}
                    </span>
                  </button>
                </header>
                <table className="yard-material-summary-table">
                  <thead>
                    <tr>
                      <th>Fraktion/Material</th>
                      <th>Anzahl LKW Parkplatz</th>
                      <th>Anzahl LKW Werk</th>
                    </tr>
                  </thead>
                  <tbody>
                    {materialSummary.map((row) => (
                      <tr key={row.label}>
                        <td>{row.label}</td>
                        <td className="yard-material-summary-count">{row.countParkplatz}</td>
                        <td className="yard-material-summary-count">{row.countWerk}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>

              <section className={`yard-list-filters ${isFilterCollapsed ? 'yard-list-filters--collapsed' : ''}`} aria-label={PAGE_TEXT.filters.title}>
                <form className="yard-list-filter-form" onSubmit={handleFilterSubmit}>
                  <header className="yard-list-filter-header">
                    <h2>{PAGE_TEXT.filters.title}</h2>
                    <button
                      type="button"
                      className="yard-list-filter-toggle"
                      onClick={toggleFilterCollapse}
                      aria-expanded={!isFilterCollapsed}
                      aria-label={isFilterCollapsed ? "Filter einblenden" : "Filter ausblenden"}
                    >
                      <span className="yard-list-filter-toggle-icon" aria-hidden="true">
                        {isFilterCollapsed ? "▼" : "▲"}
                      </span>
                    </button>
                  </header>
                  <div className="yard-list-filter-grid">
                    <div className="yard-list-filter-field">
                      <label className="yard-list-filter-label" htmlFor="filter-fraction">
                        {PAGE_TEXT.filters.fraction}
                      </label>
                      <input
                        id="filter-fraction"
                        className="yard-list-filter-input"
                        type="text"
                        value={filters.fraction}
                        onChange={handleFilterChange("fraction")}
                        placeholder={PAGE_TEXT.filters.multiHint}
                      />
                    </div>
                    <div className="yard-list-filter-field">
                      <label className="yard-list-filter-label" htmlFor="filter-status">
                        {PAGE_TEXT.filters.status}
                      </label>
                      <div className="yard-list-status-dropdown" data-dropdown="status">
                        <button
                          type="button"
                          id="filter-status"
                          className="yard-list-status-dropdown-button"
                          onClick={() => setStatusDropdownOpen((prev) => !prev)}
                          aria-haspopup="listbox"
                          aria-expanded={statusDropdownOpen}
                        >
                          <span className="yard-list-status-dropdown-text">
                            {filters.statuses.length === 0
                              ? "Alle Status"
                              : `${filters.statuses.length} ausgewählt`}
                          </span>
                          <span className="yard-list-status-dropdown-icon" aria-hidden="true">
                            {statusDropdownOpen ? "▲" : "▼"}
                          </span>
                        </button>
                        {statusDropdownOpen && (
                          <div className="yard-list-status-dropdown-menu" role="listbox">
                            {Object.entries(YARD_STATUS_LABELS).map(([value, label]) => (
                              <label key={value} className="yard-list-status-dropdown-item">
                                <input
                                  type="checkbox"
                                  className="yard-list-status-dropdown-checkbox"
                                  value={value}
                                  checked={filters.statuses.includes(value)}
                                  onChange={(e) => {
                                    setFilters((current) => ({
                                      ...current,
                                      statuses: e.target.checked
                                        ? [...current.statuses, value]
                                        : current.statuses.filter((s) => s !== value)
                                    }));
                                  }}
                                />
                                <span>{value} - {label}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="yard-list-filter-field">
                      <label className="yard-list-filter-label" htmlFor="filter-timing-status">
                        {PAGE_TEXT.filters.timingStatus}
                      </label>
                      <select
                        id="filter-timing-status"
                        className="yard-list-filter-input"
                        value={filters.timingStatusFilter || ''}
                        onChange={(e) => {
                          setFilters(prev => ({
                            ...prev,
                            timingStatusFilter: e.target.value
                          }));
                        }}
                      >
                        <option value="">Alle</option>
                        <option value="green">Grün (Pünktlich)</option>
                        <option value="yellow">Gelb (Leichte Abweichung)</option>
                        <option value="red">Rot (Starke Abweichung)</option>
                      </select>
                    </div>
                    <div className="yard-list-filter-field">
                      <label className="yard-list-filter-label" htmlFor="filter-carrier">
                        {PAGE_TEXT.filters.carrierName}
                      </label>
                      <input
                        id="filter-carrier"
                        className="yard-list-filter-input"
                        type="text"
                        value={filters.carrierName}
                        onChange={handleFilterChange("carrierName")}
                      />
                    </div>
                    <div className="yard-list-filter-field">
                      <label className="yard-list-filter-label" htmlFor="filter-supplier">
                        {PAGE_TEXT.filters.supplierName}
                      </label>
                      <input
                        id="filter-supplier"
                        className="yard-list-filter-input"
                        type="text"
                        value={filters.supplierName}
                        onChange={handleFilterChange("supplierName")}
                      />
                    </div>
                    <div className="yard-list-filter-field">
                      <label className="yard-list-filter-label" htmlFor="filter-transport-batch">
                        {PAGE_TEXT.filters.transportNumber}
                      </label>
                      <input
                        id="filter-transport-batch"
                        className="yard-list-filter-input"
                        type="text"
                        value={filters.transportNumber}
                        onChange={handleFilterChange("transportNumber")}
                        placeholder="z.B. 1000033960001"
                      />
                    </div>
                    <div className="yard-list-filter-field">
                      <label className="yard-list-filter-label" htmlFor="filter-sortiment">
                        {PAGE_TEXT.filters.sortiment}
                      </label>
                      <input
                        id="filter-sortiment"
                        className="yard-list-filter-input"
                        type="text"
                        value={filters.sortiment}
                        onChange={handleFilterChange("sortiment")}
                        placeholder="z.B. Buche"
                      />
                    </div>
                    <div className="yard-list-filter-field">
                      <label className="yard-list-filter-label" htmlFor="filter-arrival-range">
                        {PAGE_TEXT.filters.arrivalTimeRange}
                      </label>
                      <div style={{ position: 'relative' }}>
                        <input
                          id="filter-arrival-range"
                          className="yard-list-filter-input"
                          type="text"
                          readOnly
                          value={
                            filters.createdAtFrom && filters.createdAtTo
                              ? `${new Date(filters.createdAtFrom).toLocaleDateString('de-DE')} ${new Date(filters.createdAtFrom).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} - ${new Date(filters.createdAtTo).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`
                              : ''
                          }
                          placeholder="Klicken zum Auswählen"
                          onClick={(e) => {
                            const dropdown = (e.currentTarget.nextElementSibling as HTMLElement);
                            if (dropdown) {
                              dropdown.style.display = dropdown.style.display === 'none' ? 'block' : 'none';
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        />
                        <div
                          style={{
                            display: 'none',
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            background: 'white',
                            border: '1px solid #d1d5db',
                            borderRadius: '0.5rem',
                            padding: '1rem',
                            marginTop: '0.25rem',
                            zIndex: 1000,
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                          }}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            <div>
                              <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem', color: '#374151' }}>Datum</label>
                              <input
                                type="date"
                                className="yard-list-filter-input"
                                value={filters.createdAtFrom ? filters.createdAtFrom.slice(0, 10) : ''}
                                onChange={(e) => {
                                  const dateStr = e.target.value;
                                  setFilters(prev => {
                                    const timeFrom = prev.createdAtFrom ? prev.createdAtFrom.slice(11, 16) : '00:00';
                                    const timeTo = prev.createdAtTo ? prev.createdAtTo.slice(11, 16) : '23:59';
                                    return {
                                      ...prev,
                                      createdAtFrom: dateStr ? `${dateStr}T${timeFrom}` : '',
                                      createdAtTo: dateStr ? `${dateStr}T${timeTo}` : ''
                                    };
                                  });
                                }}
                              />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                              <div>
                                <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem', color: '#374151' }}>Von</label>
                                <input
                                  type="time"
                                  className="yard-list-filter-input"
                                  value={filters.createdAtFrom ? filters.createdAtFrom.slice(11, 16) : ''}
                                  onChange={(e) => {
                                    const timeStr = e.target.value;
                                    setFilters(prev => {
                                      const dateStr = prev.createdAtFrom ? prev.createdAtFrom.slice(0, 10) : new Date().toISOString().slice(0, 10);
                                      return {
                                        ...prev,
                                        createdAtFrom: timeStr && dateStr ? `${dateStr}T${timeStr}` : ''
                                      };
                                    });
                                  }}
                                />
                              </div>
                              <div>
                                <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem', color: '#374151' }}>Bis</label>
                                <input
                                  type="time"
                                  className="yard-list-filter-input"
                                  value={filters.createdAtTo ? filters.createdAtTo.slice(11, 16) : ''}
                                  onChange={(e) => {
                                    const timeStr = e.target.value;
                                    setFilters(prev => {
                                      const dateStr = prev.createdAtTo ? prev.createdAtTo.slice(0, 10) : (prev.createdAtFrom ? prev.createdAtFrom.slice(0, 10) : new Date().toISOString().slice(0, 10));
                                      return {
                                        ...prev,
                                        createdAtTo: timeStr && dateStr ? `${dateStr}T${timeStr}` : ''
                                      };
                                    });
                                  }}
                                />
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                              <button
                                type="button"
                                onClick={(e) => {
                                  const dropdown = (e.currentTarget.closest('div[style*="position: absolute"]') as HTMLElement);
                                  if (dropdown) dropdown.style.display = 'none';
                                }}
                                style={{
                                  flex: 1,
                                  padding: '0.5rem',
                                  background: '#1f6feb',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '0.375rem',
                                  cursor: 'pointer',
                                  fontSize: '0.875rem',
                                  fontWeight: '500'
                                }}
                              >
                                Übernehmen
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setFilters(prev => ({ ...prev, createdAtFrom: '', createdAtTo: '' }));
                                }}
                                style={{
                                  padding: '0.5rem 0.75rem',
                                  background: '#f3f4f6',
                                  color: '#374151',
                                  border: 'none',
                                  borderRadius: '0.375rem',
                                  cursor: 'pointer',
                                  fontSize: '0.875rem'
                                }}
                              >
                                Zurücksetzen
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="yard-list-filter-field">
                      <label className="yard-list-filter-label" htmlFor="filter-timewindow-range">
                        {PAGE_TEXT.filters.timeWindowRange}
                      </label>
                      <div style={{ position: 'relative' }}>
                        <input
                          id="filter-timewindow-range"
                          className="yard-list-filter-input"
                          type="text"
                          readOnly
                          value={
                            filters.timeWindowToFrom && filters.timeWindowToTo
                              ? `${new Date(filters.timeWindowToFrom).toLocaleDateString('de-DE')} ${new Date(filters.timeWindowToFrom).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} - ${new Date(filters.timeWindowToTo).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`
                              : ''
                          }
                          placeholder="Klicken zum Auswählen"
                          onClick={(e) => {
                            const dropdown = (e.currentTarget.nextElementSibling as HTMLElement);
                            if (dropdown) {
                              dropdown.style.display = dropdown.style.display === 'none' ? 'block' : 'none';
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        />
                        <div
                          style={{
                            display: 'none',
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            background: 'white',
                            border: '1px solid #d1d5db',
                            borderRadius: '0.5rem',
                            padding: '1rem',
                            marginTop: '0.25rem',
                            zIndex: 1000,
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                          }}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            <div>
                              <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem', color: '#374151' }}>Datum</label>
                              <input
                                type="date"
                                className="yard-list-filter-input"
                                value={filters.timeWindowToFrom ? filters.timeWindowToFrom.split('T')[0] : ''}
                                onChange={(e) => {
                                  const date = e.target.value;
                                  const time = filters.timeWindowToFrom ? filters.timeWindowToFrom.split('T')[1] || '00:00' : '00:00';
                                  setFilters(prev => ({ ...prev, timeWindowToFrom: date ? `${date}T${time}` : '' }));
                                }}
                              />
                            </div>
                            <div style={{ display: 'flex', gap: '0.75rem' }}>
                              <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem', color: '#374151' }}>Von</label>
                                <input
                                  type="time"
                                  className="yard-list-filter-input"
                                  value={filters.timeWindowToFrom ? filters.timeWindowToFrom.split('T')[1] || '' : ''}
                                  onChange={(e) => {
                                    const time = e.target.value;
                                    const date = filters.timeWindowToFrom ? filters.timeWindowToFrom.split('T')[0] : new Date().toISOString().split('T')[0];
                                    setFilters(prev => ({ ...prev, timeWindowToFrom: `${date}T${time}` }));
                                  }}
                                />
                              </div>
                              <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', fontSize: '0.875rem', marginBottom: '0.25rem', color: '#374151' }}>Bis</label>
                                <input
                                  type="time"
                                  className="yard-list-filter-input"
                                  value={filters.timeWindowToTo ? filters.timeWindowToTo.split('T')[1] || '' : ''}
                                  onChange={(e) => {
                                    const time = e.target.value;
                                    const date = filters.timeWindowToFrom ? filters.timeWindowToFrom.split('T')[0] : new Date().toISOString().split('T')[0];
                                    setFilters(prev => ({ ...prev, timeWindowToTo: `${date}T${time}` }));
                                  }}
                                />
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                              <button
                                type="button"
                                onClick={(e) => {
                                  const dropdown = (e.currentTarget.closest('[style*="position: absolute"]') as HTMLElement);
                                  if (dropdown) dropdown.style.display = 'none';
                                }}
                                style={{
                                  flex: 1,
                                  padding: '0.5rem',
                                  background: '#1f6feb',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '0.375rem',
                                  cursor: 'pointer',
                                  fontSize: '0.875rem',
                                  fontWeight: '500'
                                }}
                              >
                                Übernehmen
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setFilters(prev => ({ ...prev, timeWindowToFrom: '', timeWindowToTo: '' }));
                                }}
                                style={{
                                  padding: '0.5rem 0.75rem',
                                  background: '#f3f4f6',
                                  color: '#374151',
                                  border: 'none',
                                  borderRadius: '0.375rem',
                                  cursor: 'pointer',
                                  fontSize: '0.875rem'
                                }}
                              >
                                Zurücksetzen
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="yard-list-filter-actions">
                    {/* Varianten-Auswahl */}
                    <div className="yard-list-filter-variants">
                      <label htmlFor="variant-select" className="yard-list-filter-variants-label">
                        Ansicht:
                      </label>
                      <select
                        id="variant-select"
                        className="yard-list-filter-variants-select"
                        value={selectedVariantId || ""}
                        onChange={(e) => handleVariantSelect(e.target.value)}
                        disabled={state.status === "loading"}
                      >
                        <option value="">{PAGE_TEXT.variants.select}</option>
                        {Array.isArray(variants) && variants.map((variant) => (
                          <option key={variant._id} value={variant._id}>
                            {variant.name} {variant.isDefault ? `(${PAGE_TEXT.variants.default})` : ""}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="yard-list-filter-variants-icon-button"
                        onClick={handleOpenSaveVariantDialog}
                        disabled={state.status === "loading"}
                        title={PAGE_TEXT.variants.save}
                      >
                        💾
                      </button>
                      {selectedVariantId && (
                        <button
                          type="button"
                          className="yard-list-filter-variants-icon-button"
                          onClick={() => handleDeleteVariant(selectedVariantId)}
                          disabled={state.status === "loading"}
                          title={PAGE_TEXT.variants.deleteButton}
                        >
                          🗑️
                        </button>
                      )}
                    </div>

                    {/* Filter-Buttons */}
                    <div className="yard-list-filter-buttons">
                      <button
                        type="button"
                        className="yard-list-filter-reset"
                        onClick={handleFilterReset}
                        disabled={!hasActiveFilters && isFilterFormPristine}
                      >
                        {PAGE_TEXT.filters.reset}
                      </button>
                      <button type="submit" className="yard-list-filter-apply">
                        {PAGE_TEXT.filters.apply}
                      </button>
                    </div>
                  </div>
                </form>
              </section>
              {state.status === "error" ? (
                <div className="yard-list-state yard-list-state-error" role="alert">
                  <span className="yard-list-state-icon" aria-hidden="true">!</span>
                  <div className="yard-list-state-body">
                    <h2>Fehler beim Laden</h2>
                    <p>{state.error ?? PAGE_TEXT.error}</p>
                  </div>
                </div>
              ) : isInitialLoading ? (
                <div className="yard-list-state yard-list-state-loading" role="status" aria-live="polite">
                  <span className="yard-list-spinner" aria-hidden="true" />
                  <p>{PAGE_TEXT.loading}</p>
                </div>
              ) : pendingActionRowId ? (
                <div className="yard-list-state yard-list-state-loading" role="status" aria-live="polite">
                  <span className="yard-list-spinner" aria-hidden="true" />
                  <p>{PAGE_TEXT.actionInProgress}</p>
                </div>
              ) : showEmptyState ? (
                <div className="yard-list-state yard-list-state-empty">
                  <h2>{PAGE_TEXT.emptyTitle}</h2>
                  <p>{PAGE_TEXT.emptyDescription}</p>
                </div>
              ) : (
                <div className="yard-list-table-wrapper">
                  <table className="yard-list-table">
                    <colgroup>
                      <col key="col-action" style={{ width: "2.5rem" }} />
                      {COLUMNS.map((column) => (
                        <col
                          key={`col-${String(column.key)}`}
                          style={column.width ? { width: `${column.width}px` } : undefined}
                        />
                      ))}
                    </colgroup>
                    <thead className="yard-list-table-head">
                      <tr>
                        <th className="yard-list-action-header" aria-label="Aktionen">
                          <button
                            type="button"
                            className="yard-list-refresh-icon-button"
                            onClick={handleRefresh}
                            disabled={state.status === "loading" || Boolean(pendingActionRowId)}
                            aria-label="Aktualisieren"
                            title="Aktualisieren"
                          >
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="23 4 23 10 17 10"></polyline>
                              <polyline points="1 20 1 14 7 14"></polyline>
                              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                            </svg>
                          </button>
                        </th>
                        {COLUMNS.map((column) => (
                          <th key={String(column.key)} className="yard-list-header-cell">
                            {column.type === "status" || column.sortable === false ? (
                              <button
                                type="button"
                                className="yard-list-sort-button"
                                disabled
                              >
                                <span className="yard-list-sort-label">{column.label}</span>
                              </button>
                            ) : (
                              <button
                                type="button"
                                className={buildSortButtonClass(column, sortConfig)}
                                onClick={() => toggleSort(column)}
                              >
                                <span className="yard-list-sort-label">{column.label}</span>
                                <span className="yard-list-sort-icon" aria-hidden="true">
                                  {renderSortIcon(column, sortConfig)}
                                </span>
                              </button>
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {visibleItems.map((document) => (
                        <tr
                          key={String(document._id)}
                          className="yard-list-row"
                        >
                          <td className="yard-list-action-cell">
                            {String(document.LKW_LOAD_STATUS) === "1" && (
                              <button
                                type="button"
                                className="yard-list-abruf-button"
                                aria-label="Abruf"
                                disabled={Boolean(pendingActionRowId)}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleAbruf(document);
                                }}
                              >
                                Abruf
                              </button>
                            )}
                          </td>
                          {COLUMNS.map((column) => (
                            <td
                              key={`${document._id}-${String(column.key)}`}
                              className={`yard-list-cell${column.type === "status" ? " yard-list-cell-status" : ""}${column.key === "SORTIMENT" || column.key === "SPEDITION" || column.key === "LIEFERANT" ? " yard-list-cell-wrap" : ""}`}
                            >
                              {renderCell(document, column, language)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </section>
      </main>
      {activeWeightConfirmation ? (
        <div className="yard-weight-confirmation-overlay" role="presentation">
          <div
            className="yard-weight-confirmation-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="yard-weight-confirmation-title"
            aria-describedby="yard-weight-confirmation-description"
          >
            <header className="yard-weight-confirmation-header">
              <h2 id="yard-weight-confirmation-title">{WEIGHT_CONFIRMATION_TEXT.title}</h2>
              <p id="yard-weight-confirmation-description">{WEIGHT_CONFIRMATION_TEXT.description}</p>
            </header>
            <dl className="yard-weight-confirmation-details">
              <div className="yard-weight-confirmation-detail">
                <dt>{WEIGHT_CONFIRMATION_TEXT.transportLabel}</dt>
                <dd>{activeWeightConfirmation.transportBatchNumber}</dd>
              </div>
              <div className="yard-weight-confirmation-detail">
                <dt>{WEIGHT_CONFIRMATION_TEXT.processLabel}</dt>
                <dd>{WEIGHT_CONFIRMATION_TEXT.direction[activeWeightConfirmation.direction]}</dd>
              </div>
              <div className="yard-weight-confirmation-detail">
                <dt>{WEIGHT_CONFIRMATION_TEXT.licensePlateLabel}</dt>
                <dd>{activeWeightConfirmation.licensePlate?.trim() || "–"}</dd>
              </div>
              <div className="yard-weight-confirmation-detail">
                <dt>{WEIGHT_CONFIRMATION_TEXT.weightLabel}</dt>
                <dd>
                  {isEditingWeight ? (
                    <div className="yard-weight-edit-container">
                      <input
                        type="number"
                        className="yard-weight-input"
                        value={editedWeight}
                        onChange={handleWeightInputChange}
                        min="0"
                        step="0.01"
                        autoFocus
                      />
                      <span className="yard-weight-unit">kg</span>
                      <span className="yard-weight-original">
                        (Original: {formattedWeight} kg)
                      </span>
                    </div>
                  ) : (
                    <div className="yard-weight-readonly-container">
                      <span className="yard-weight-value">
                        {formattedWeight ? `${formattedWeight} kg` : "–"}
                      </span>
                      <button
                        type="button"
                        className="yard-weight-edit-button"
                        onClick={handleEditWeight}
                        disabled={isConfirmingWeight}
                      >
                        {WEIGHT_CONFIRMATION_TEXT.editButton}
                      </button>
                    </div>
                  )}
                </dd>
              </div>
              <div className="yard-weight-confirmation-detail">
                <dt>{WEIGHT_CONFIRMATION_TEXT.createdAtLabel}</dt>
                <dd>{formattedCreatedAt}</dd>
              </div>
            </dl>
            {queueMessage ? <p className="yard-weight-confirmation-queue-hint">{queueMessage}</p> : null}
            {weightConfirmationFetchError ? (
              <div className="yard-weight-confirmation-error" role="alert">
                {weightConfirmationFetchError}
              </div>
            ) : null}
            {weightConfirmationActionError ? (
              <div className="yard-weight-confirmation-error" role="alert">
                {weightConfirmationActionError}
              </div>
            ) : null}
            <div className="yard-weight-confirmation-actions">
              {isEditingWeight ? (
                <>
                  <button
                    type="button"
                    className="yard-weight-confirmation-button-secondary"
                    onClick={handleCancelEditWeight}
                    disabled={isConfirmingWeight}
                  >
                    {WEIGHT_CONFIRMATION_TEXT.cancelButton}
                  </button>
                  <button
                    type="button"
                    className="yard-weight-confirmation-button"
                    onClick={handleConfirmWeight}
                    disabled={isConfirmingWeight || editedWeight.trim() === "" || parseFloat(editedWeight) <= 0}
                  >
                    {isConfirmingActive
                      ? WEIGHT_CONFIRMATION_TEXT.confirmingButton
                      : WEIGHT_CONFIRMATION_TEXT.saveButton}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="yard-weight-confirmation-button"
                  onClick={handleConfirmWeight}
                  disabled={isConfirmingWeight}
                >
                  {isConfirmingActive
                    ? WEIGHT_CONFIRMATION_TEXT.confirmingButton
                    : WEIGHT_CONFIRMATION_TEXT.confirmButton}
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function isFilterFormEmpty(form: FilterFormState): boolean {
  return (
    form.transportNumber.trim().length === 0 &&
    form.fraction.trim().length === 0 &&
    form.carrierName.trim().length === 0 &&
    form.supplierName.trim().length === 0 &&
    form.statuses.length === 0 &&
    form.timeWindowFromFrom.trim().length === 0 &&
    form.timeWindowFromTo.trim().length === 0 &&
    form.createdAtFrom.trim().length === 0 &&
    form.createdAtTo.trim().length === 0
  );
}

function appendFiltersToSearchParams(params: URL | URLSearchParams, filters: AppliedFilters): void {
  // URLSearchParams direkt verwenden oder von URL extrahieren
  const searchParams = params instanceof URL ? params.searchParams : params;

  // WICHTIG: Nur Dokumente mit Transportchargennummer laden
  searchParams.set("filter[TRANSPORTCHARGE][$exists]", "true");

  // WICHTIG: Nur Status 1-7 anzeigen (Check-In bis Check-Out, nicht Warehouse Transports)
  // ABER: Nur wenn KEINE spezifischen Status ausgewählt sind (sonst $in Filter verwenden)
  if (!filters?.statuses || filters.statuses.length === 0) {
    searchParams.set("filter[LKW_LOAD_STATUS][$lte]", "7");
  }

  if (!filters) {
    return;
  }

  if (filters.transportNumber) {
    searchParams.set("filter[LKW_LOAD_ID]", filters.transportNumber);
  }

  if (filters.carrierName) {
    searchParams.set("filter[carrierName]", filters.carrierName);
  }

  if (filters.supplierName) {
    searchParams.set("filter[SupplierName]", filters.supplierName);
  }

  if (filters.sortiment) {
    searchParams.set("filter[Sortimentbez]", filters.sortiment);
  }

  if (filters.fractions && filters.fractions.length > 0) {
    filters.fractions.forEach((fraction) => {
      searchParams.append("filter[FRAKTION][]", fraction);
    });
  }

  if (filters.statuses && filters.statuses.length > 0) {
    filters.statuses.forEach((status) => {
      searchParams.append("filter[LKW_LOAD_STATUS][]", String(status));
    });
  }

  if (filters.createdAtFrom) {
    searchParams.set("filter[createdAt][from]", filters.createdAtFrom);
  }

  if (filters.createdAtTo) {
    searchParams.set("filter[createdAt][to]", filters.createdAtTo);
  }

  if (filters.timeWindowToFrom) {
    searchParams.set("filter[TERMIN_BIS][from]", filters.timeWindowToFrom);
  }

  if (filters.timeWindowToTo) {
    searchParams.set("filter[TERMIN_BIS][to]", filters.timeWindowToTo);
  }
}

function renderCell(document: YardDocument, column: ColumnDefinition, language: string): ReactElement {
  const value = document[column.key];

  // SAP LKW_LOAD_STATUS handling
  if (column.key === "LKW_LOAD_STATUS") {
    const stringValue = typeof value === "string" ? value.trim() : String(value || "");

    if (stringValue.length > 0) {
      const label = YARD_STATUS_LABELS[stringValue];
      if (label) {
        return <span>{label}</span>;
      }
      return <span>{stringValue}</span>;
    }

    return <span>–</span>;
  }

  // Legacy status field (backward compatibility)
  if (column.key === "status") {
    const numericValue =
      typeof value === "number"
        ? value
        : typeof value === "string" && value.trim().length > 0
          ? Number.parseInt(value, 10)
          : null;

    if (numericValue != null && Number.isFinite(numericValue)) {
      const label = YARD_STATUS_LABELS[String(numericValue)];
      if (label) {
        return <span>{label}</span>;
      }

      return <span>{numericValue}</span>;
    }

    return <span>–</span>;
  }

  if (column.type === "status") {
    const status = formatTimingStatus(isTimingStatus(value) ? value : null);
    const title = status.description ?? status.label;
    return (
      <span className={`ui-status-indicator ui-status-${status.color}`} title={title} aria-label={title}>
        <span className="ui-status-dot" aria-hidden="true" />
        <span className="ui-status-label">{status.label}</span>
      </span>
    );
  }

  if (column.key === "truckLicensePlate" || column.key === "KENNZEICHEN") {
    const normalizedLicensePlate = normalizeLicensePlate(value);
    if (normalizedLicensePlate !== null) {
      return <span>{normalizedLicensePlate}</span>;
    }
  }

  if (column.key === "FRAKTION") {
    // Zeige den Namen (fraction) statt der SAP-Nummer (FRAKTION)
    const fractionName = document.fraction || document.FRAKTION;
    if (fractionName) {
      return <span>{String(fractionName)}</span>;
    }
  }

  if (column.key === "SORTIMENT") {
    // Zeige Sortimentbez (alter Feldname) für SORTIMENT
    const sortimentValue = document.Sortimentbez || document.SORTIMENT;
    if (sortimentValue) {
      return <span>{String(sortimentValue)}</span>;
    }
  }

  if (column.key === "SPEDITION") {
    // Zeige carrierName (alter Feldname) für SPEDITION
    const speditionValue = document.carrierName || document.SPEDITION;
    if (speditionValue) {
      return <span>{String(speditionValue)}</span>;
    }
  }

  if (column.key === "LIEFERANT") {
    // Zeige SupplierName (alter Feldname) für LIEFERANT
    const lieferantValue = document.SupplierName || document.LIEFERANT;
    if (lieferantValue) {
      return <span>{String(lieferantValue)}</span>;
    }
  }

  if (column.key === "timeWindow") {
    // Zeige Zeitfenster (TERMIN_VON - TERMIN_BIS) als Datum + Uhrzeit
    const terminVon = document.TERMIN_VON;
    const terminBis = document.TERMIN_BIS;

    if (terminVon && terminBis && typeof terminVon === 'string' && typeof terminBis === 'string') {
      const dateVon = new Date(terminVon);
      const dateBis = new Date(terminBis);

      // Format: DD.MM.YYYY HH:MM - HH:MM
      const dateStr = dateVon.toLocaleDateString(language, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      const timeVon = dateVon.toLocaleTimeString(language, {
        hour: '2-digit',
        minute: '2-digit'
      });
      const timeBis = dateBis.toLocaleTimeString(language, {
        hour: '2-digit',
        minute: '2-digit'
      });

      return <span>{`${dateStr} ${timeVon} - ${timeBis}`}</span>;
    }
    return <span>–</span>;
  }

  if (column.key === "createdAt") {
    // Zeige nur die Uhrzeit (ohne Datum und Sekunden)
    const createdAt = document.createdAt;
    if (createdAt) {
      const date = new Date(createdAt);
      const timeString = date.toLocaleTimeString(language, {
        hour: '2-digit',
        minute: '2-digit'
      });
      return <span>{timeString}</span>;
    }
    return <span>–</span>;
  }

  if (column.key === "waitTime") {
    // Berechne Wartezeit in HH:MM Format
    // Nur wenn Transport über PreCheckin läuft (Status begann bei "1")
    // Bei Status >= 2 nutze calledAt als Endzeit, sonst aktuelle Zeit
    const createdAt = document.createdAt;
    const status = document.LKW_LOAD_STATUS;

    // Zeige Wartezeit nur an, wenn:
    // 1. createdAt existiert (PreCheckin wurde durchgeführt)
    // 2. Status ist "1" (Pre-Check-In) ODER Status >= "2" mit calledAt (Abruf erfolgte nach PreCheckin)
    const hasPreCheckin = createdAt && (status === "1" || (status && parseInt(status) >= 2 && document.calledAt));

    if (hasPreCheckin && createdAt) {
      const preCheckinTime = new Date(createdAt);
      let endTime: Date;

      // Wenn Status >= "2" und calledAt existiert, nutze calledAt als Endzeit
      if (status && parseInt(status) >= 2 && document.calledAt) {
        endTime = new Date(document.calledAt);
      } else {
        // Sonst nutze aktuelle Zeit (fortlaufend)
        endTime = new Date();
      }

      const diffMs = endTime.getTime() - preCheckinTime.getTime();
      const diffMinutes = Math.floor(diffMs / 60000);

      if (diffMinutes >= 0) {
        const hours = Math.floor(diffMinutes / 60);
        const minutes = diffMinutes % 60;
        const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        return <span>{timeString}</span>;
      }
    }
    return <span>–</span>;
  }

  const descriptor = column.format ?? null;
  const displayValue = formatResultValue(value, language, descriptor);
  return <span>{displayValue}</span>;
}

function buildSortButtonClass(column: ColumnDefinition, sortConfig: SortConfig | null): string {
  const base = "yard-list-sort-button";
  if (!sortConfig || sortConfig.key !== column.key) {
    return base;
  }
  return `${base} yard-list-sort-button--${sortConfig.direction}`;
}

function renderSortIcon(column: ColumnDefinition, sortConfig: SortConfig | null): string {
  if (!sortConfig || sortConfig.key !== column.key) {
    return "↕";
  }
  return sortConfig.direction === "asc" ? "▲" : "▼";
}

async function extractErrorMessage(response: Response): Promise<string | undefined> {
  const contentType = response.headers.get("content-type") ?? "";

  try {
    if (contentType.includes("application/json")) {
      const payload = (await response.json()) as Record<string, unknown> | null;
      const messageRaw = payload && typeof payload.message === "string" ? payload.message.trim() : "";
      if (messageRaw) {
        return messageRaw;
      }
    } else {
      const text = await response.text();
      const trimmed = text.trim();
      if (trimmed.length > 0) {
        return trimmed;
      }
    }
  } catch {
    return undefined;
  }

  return undefined;
}

function normalizeLicensePlate(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.replace(/[\s-]+/g, "");
  return normalized.length > 0 ? normalized : null;
}
